import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Volume2, VolumeX, Loader2, Play, Pause } from 'lucide-react';
import { PEOPLE } from '../constants';

const VideoPage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const person = PEOPLE.find(p => p.id === personId);
  
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsLoading(true);
    setVideoError(false);
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [personId]);

  if (!person) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-black">
        <h1 className="text-4xl font-black mb-4 text-white uppercase tracking-tighter italic text-center px-4">Memory Not Found</h1>
        <button onClick={() => navigate('/')} className="px-8 py-4 bg-white text-black font-black rounded-full uppercase text-xs tracking-widest">Back to Feed</button>
      </div>
    );
  }

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  const showPlaceholder = !person.videoUrl || videoError;

  return (
    <div className="h-screen w-full relative bg-black overflow-hidden font-montserrat">
      {/* Static Color Glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${person.color} opacity-10 blur-[120px] pointer-events-none`} />

      {/* Header Controls */}
      <div className="absolute top-12 left-0 right-0 z-50 flex items-center justify-between px-8">
        <button 
          onClick={() => navigate('/')} 
          className="p-4 bg-zinc-900/50 backdrop-blur-xl rounded-full border border-white/10 text-white"
        >
          <ChevronLeft size={24} />
        </button>
        
        <div className="text-center absolute left-1/2 -translate-x-1/2">
          <p className="text-[9px] uppercase tracking-[0.4em] font-black text-white/40 mb-1">BEACHHOUSE 2025 WRAPPED</p>
          <h2 className="text-2xl font-black text-white tracking-tighter italic">{person.name}</h2>
        </div>

        {/* Empty div for layout balance since share button was removed */}
        <div className="w-[58px]" />
      </div>

      {/* Centered Video Player */}
      <div className="h-full w-full flex items-center justify-center p-6">
        <div className="w-full h-full max-w-[500px] relative rounded-3xl overflow-hidden shadow-2xl bg-zinc-950 border border-white/5">
          <AnimatePresence mode="wait">
            {!showPlaceholder ? (
              <motion.div 
                key="video-content"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="w-full h-full relative"
              >
                {isLoading && (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/90">
                    <Loader2 className="w-12 h-12 text-white/20 animate-spin mb-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/20">Loading Archive</span>
                  </div>
                )}
                
                {/* Background Blur Layer */}
                <video 
                  src={person.videoUrl}
                  className="absolute inset-0 w-full h-full object-cover scale-110 opacity-30 blur-3xl pointer-events-none"
                  autoPlay playsInline loop muted
                />
                
                {/* Primary Video */}
                <video 
                  ref={videoRef}
                  src={person.videoUrl}
                  className="w-full h-full object-contain relative z-20"
                  autoPlay playsInline loop
                  muted={isMuted}
                  onWaiting={() => setIsLoading(true)}
                  onCanPlay={() => setIsLoading(false)}
                  onPlaying={() => setIsLoading(false)}
                  onError={() => setVideoError(true)}
                />
                
                {/* Tap to Toggle Play Overlay */}
                <div 
                  className="absolute inset-0 z-40 cursor-pointer flex items-center justify-center" 
                  onClick={togglePlay}
                >
                  <AnimatePresence>
                    {!isPlaying && (
                      <motion.div 
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        className="p-8 bg-black/40 backdrop-blur-md rounded-full border border-white/20 text-white"
                      >
                        <Play size={48} fill="currentColor" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Status Indicator / Mute Toggle */}
                <div className="absolute bottom-10 right-10 z-50 flex flex-col gap-4">
                  <button 
                    onClick={togglePlay}
                    className="p-3 bg-black/50 backdrop-blur-xl rounded-full border border-white/10 text-white hover:bg-black/70 transition-colors"
                  >
                    {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
                  </button>
                  <button 
                    onClick={toggleMute}
                    className="p-3 bg-black/50 backdrop-blur-xl rounded-full border border-white/10 text-white hover:bg-black/70 transition-colors"
                  >
                    {isMuted ? <VolumeX size={20} className="text-white/40" /> : <Volume2 size={20} className="text-green-500" />}
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-12 bg-zinc-900">
                <div className="text-[15rem] font-black text-white/5 mb-8">{person.name[0]}</div>
                <h3 className="text-3xl font-black mb-4 uppercase">Archive Missing</h3>
                <p className="text-sm text-white/40 font-medium">Please ensure {person.videoUrl} exists in the /public/videos folder.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Audio Prompt */}
      <AnimatePresence>
        {isMuted && !isLoading && !showPlaceholder && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            onClick={toggleMute}
            className="absolute bottom-32 left-1/2 -translate-x-1/2 z-[60] px-8 py-4 bg-white text-black rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl cursor-pointer"
          >
            Tap to enable sound
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoPage;
